import type { Dictionary, EntityState } from '@reduxjs/toolkit';
import {
	createAsyncThunk,
	createEntityAdapter,
	createSlice,
} from '@reduxjs/toolkit';
import lodash from 'lodash';
import { ApiRequestError, ApiServerError, unwrapFetch2 } from '~/fetch';
import { isDssiSiteType } from '~/helpers/User';
import type {
	GetProductUsageArgs,
	GetProductUsageErrorResult,
	GetProductUsageResult,
	ProductUsage,
} from '~/ogm-hub';
import { ProductUsageController } from '~/ogm-hub';
import type { IAppState } from '~/appstate';
import { LocalStorage } from '../helpers';

export interface CustomerProductUsage extends ProductUsage {
	corpSupplier: string;
	customerId: number;
}

export const GetProductUsageId = (productUsage: CustomerProductUsage) => {
	return [
		productUsage.customerId,
		productUsage.corpSupplier,
		productUsage.productNumber,
		productUsage.chSupplier,
		productUsage.unitOfMeasure,
	].join('|');
};

export const GetProductHeaderUsageId = (
	corpSupplierCode: string,
	supplierProductNumber: string
) => {
	return [
		LocalStorage.getCustomerId(),
		corpSupplierCode,
		supplierProductNumber,
	].join('|');
};

export const GetProductLineUsageId = (
	corpSupplierCode: string,
	supplierProductNumber: string,
	distributionCenterDssiCode: string
) => {
	return [
		LocalStorage.getCustomerId(),
		corpSupplierCode,
		supplierProductNumber,
		distributionCenterDssiCode,
	].join('|');
};

export type IProductUsageHistoryState = EntityState<CustomerProductUsage>;

export const productUsageAdaptor = createEntityAdapter<CustomerProductUsage>({
	selectId: GetProductUsageId,
});

export interface ProductsGroupedBySupplier {
	corpSupplierCode: string;
	productNumbers: string[];
	distributionCenterDssiCodes: string[];
}

export interface GetSupplierProductUsageParams {
	productsGroupedBySupplier: ProductsGroupedBySupplier[];
	blueprintId?: number;
}

export const getProductUsageHistory = createAsyncThunk<
	void,
	GetSupplierProductUsageParams,
	{ state: IAppState; rejectValue: any }
>(
	'productUsageHistory/fetchProductHeaderUsage',
	async (
		params: GetSupplierProductUsageParams,
		{ getState, dispatch, rejectWithValue }
	) => {
		if (isDssiSiteType()) {
			try {
				const state = getState();
				const customerId = LocalStorage.getCustomerId();

				const missingProducts = [];
				const corpSupplierMap = {};

				if (params.blueprintId) {
					params.productsGroupedBySupplier.forEach((request) => {
						request.productNumbers.forEach((productNumber) => {
							missingProducts.push(
								...request.distributionCenterDssiCodes.map((dc) => ({
									productNumber: productNumber,
									chSupplier: dc,
								}))
							);
							corpSupplierMap[productNumber] = request.corpSupplierCode;
						});
					});
				} else {
					params.productsGroupedBySupplier.forEach((request) => {
						request.productNumbers
							.filter(
								(productNumber) =>
									Object.keys(state.productUsageHistory.entities).find(
										(usageKey) =>
											usageKey.includes(
												`${customerId}|${request.corpSupplierCode}|${productNumber}`
											)
									) == undefined
							)
							.forEach((productNumber) => {
								missingProducts.push(
									...request.distributionCenterDssiCodes.map((dc) => ({
										productNumber: productNumber,
										chSupplier: dc,
									}))
								);
								corpSupplierMap[productNumber] = request.corpSupplierCode;
							});
					});
				}

				if (missingProducts.length > 0) {
					await dispatch(
						fetchProductUsageHistory({
							requestArgs: {
								products: missingProducts,
								blueprintId: params.blueprintId,
							},
							corpSupplierMap,
							customerId,
						})
					);
				}
			} catch (err) {
				if (err instanceof ApiRequestError) {
					return rejectWithValue(err.errors);
				}
				if (err instanceof ApiServerError) {
					return rejectWithValue(err.response);
				}
				throw err;
			}
		}
	}
);

interface fetchProductUsageHistoryArgs {
	corpSupplierMap: {};
	requestArgs: GetProductUsageArgs;
	customerId: number;
}

const fetchProductUsageHistory = createAsyncThunk<
	CustomerProductUsage[],
	fetchProductUsageHistoryArgs,
	{ state: IAppState; rejectValue: string[] | Response }
>(
	'productUsageHistory/fetchProductUsageHistory',
	async (args: fetchProductUsageHistoryArgs, { rejectWithValue }) => {
		try {
			const response = await unwrapFetch2(
				ProductUsageController.GetProductUsageHistory(args.requestArgs)
			);

			if (
				(response as GetProductUsageErrorResult).errors &&
				(response as GetProductUsageErrorResult).errors.length > 0
			) {
				return rejectWithValue((response as GetProductUsageErrorResult).errors);
			}

			const items = (response as GetProductUsageResult).productUsages;
			return items.map((item) => {
				return {
					...item,
					customerId: args.customerId,
					corpSupplier: args.corpSupplierMap[item.productNumber],
				};
			});
		} catch (err) {
			if (err instanceof ApiRequestError) {
				return rejectWithValue(err.errors);
			}
			if (err instanceof ApiServerError) {
				return rejectWithValue(err.response);
			}
			throw err;
		}
	}
);

export const productUsageSlice = createSlice({
	name: 'productUsageHistory',
	initialState: productUsageAdaptor.getInitialState(),
	reducers: {},
	extraReducers: (builder) => {
		builder.addCase(fetchProductUsageHistory.fulfilled, (state, action) => {
			if (action.payload) {
				const removeIds = [];

				action.payload.forEach((item) => {
					const productId = `${item.customerId}|${item.corpSupplier}|${item.productNumber}|${item.chSupplier}|`;
					state.ids.forEach((id) => {
						if (id.toLocaleString().startsWith(productId)) {
							removeIds.push(id);
						}
					});
				});

				productUsageAdaptor.removeMany(state, removeIds);
				productUsageAdaptor.addMany(state, action.payload);
			}
		});
	},
});

export interface ProductHeaderUsageHistory {
	corpSupplier: string;
	productNumber: string;
	totalQuantityOrdered: number;
	quantityOrderedByUm: Dictionary<number>;
}

export const selectProductHeaderUsage = (
	state: IAppState,
	productids: string[],
	dcDssiCodes?: string[]
): Dictionary<ProductHeaderUsageHistory> => {
	const headerUsage: lodash.Dictionary<ProductHeaderUsageHistory> = {};
	const customerId = LocalStorage.getCustomerId();

	productids.forEach((productId) => {
		let productHeaderUsage = null;
		const productUsages = state.productUsageHistory.ids
			.filter((id: string) => id.includes(`${customerId}|${productId}`))
			.map((id: string) => state.productUsageHistory.entities[id])
			.filter(
				(usage: CustomerProductUsage) =>
					usage.unitOfMeasure &&
					(!dcDssiCodes || dcDssiCodes.includes(usage.chSupplier))
			);

		if (productUsages && productUsages.length > 0) {
			const umGroups = lodash.groupBy(
				productUsages,
				(usage) => usage.unitOfMeasure
			);
			let totalQuantityOrdered = 0;
			const quantityOrderedByUm = {};

			lodash.keys(umGroups).forEach((um) => {
				const quantitiesOrdered = umGroups[um].reduce(
					(sum, usage) => sum + usage.quantityOrderedSinceTimeProvided,
					0
				);
				totalQuantityOrdered += quantitiesOrdered;
				quantityOrderedByUm[um] = quantitiesOrdered;
			});

			productHeaderUsage = {
				coprSupplier: productUsages[0].corpSupplier,
				productNumber: productUsages[0].productNumber,
				totalQuantityOrdered,
				quantityOrderedByUm,
			};
			headerUsage[`${customerId}|${productId}`] = productHeaderUsage;
		}
	});
	return headerUsage;
};

export interface ProductLineUsageHistory {
	corpSupplier: string;
	productNumber: string;
	distributionCenterDssiCode: string;
	totalQuantityOrdered: number;
	quantityOrderedByUm: Dictionary<number>;
}

export const selectProductLineUsage = (
	state: IAppState,
	productIds: string[],
	dcDssiCodes?: string[]
) => {
	const lineUsage: lodash.Dictionary<ProductLineUsageHistory> = {};
	const customerId = LocalStorage.getCustomerId();

	productIds.forEach((productId) => {
		const allProductUsages = state.productUsageHistory.ids
			.filter((id) => (id as string).includes(`${customerId}|${productId}`))
			.map((id) => state.productUsageHistory.entities[id])
			.filter((usage) => usage.unitOfMeasure); // remove usages where the UM is missing

		const productDcUsages = lodash.groupBy(
			allProductUsages,
			(x) => x.chSupplier
		);

		lodash
			.keys(productDcUsages)
			.filter((dc) => dcDssiCodes == null || lodash.includes(dcDssiCodes, dc))
			.forEach((dc) => {
				const productUsages = productDcUsages[dc];

				lineUsage[`${customerId}|${productId}|${dc}`] = {
					corpSupplier: productUsages[0].corpSupplier,
					productNumber: productUsages[0].productNumber,
					distributionCenterDssiCode: dc,
					totalQuantityOrdered:
						productUsages.length > 0
							? productUsages.reduce(
									(sum, x) => sum + x.quantityOrderedSinceTimeProvided,
									0
							  )
							: null,
					quantityOrderedByUm: productUsages.reduce(
						(dict, x) => ({
							...dict,
							[x.unitOfMeasure]: x.quantityOrderedSinceTimeProvided,
						}),
						{}
					),
				};
			});
	});
	return lineUsage;
};
